/**
 * Site-Wide GA4 Analytics Utility (HOA)
 *
 * Singleton class that sends custom events to GA4 via the dataLayer/gtag
 * pattern. Loaded site-wide from theme.liquid.
 *
 * ─────────────────────────────────────────────────────────────────────
 * QUICK START
 * ─────────────────────────────────────────────────────────────────────
 *
 *   window.SiteAnalytics.trackAddToCart({
 *     item_id:          '12345',
 *     item_name:        'Product Title',
 *     item_variant:     'Variant Title',
 *     source_component: 'pdp_main',
 *     price:            9.00,
 *     position:         null
 *   });
 *
 * `page_path`, `hoa_session_id`, `item_currency`, `value`, `quantity`
 * are appended automatically.
 *
 * ─────────────────────────────────────────────────────────────────────
 * DATA-ATTRIBUTE CONVENTION  (for Liquid templates)
 * ─────────────────────────────────────────────────────────────────────
 *
 *   data-track-atc              — Marker: element participates in ATC tracking
 *   data-track-item-id          — Variant id
 *   data-track-item-name        — Product title
 *   data-track-item-variant     — Variant title
 *   data-track-source           — Source component (e.g. "pdp_main")
 *   data-track-price            — Numeric price (e.g. "9.00")
 *   data-track-position         — 1-based index in a list, optional
 *   data-track-selling-plan-id  — Subscription selling plan ID, optional
 *
 * Forms that submit through the product-form custom element fire
 * `variant:added` on success — the delegated listener below picks
 * them up automatically. Non-form ATCs (e.g. waitlist, cart upsell)
 * must call trackAddToCart() explicitly.
 */

if (typeof SiteAnalytics === 'undefined') {
  const SESSION_STORAGE_KEY = '_hoa_site_session_id';
  const GA4_MEASUREMENT_ID  = 'G-P2WRCKSGE1';

  class SiteAnalytics {
    constructor() {
      this.sessionId = this.getOrCreateSessionId();
      this.initializeGA4();
      this.bindGlobalListeners();
    }

    /**
     * Loads gtag.js once and configures the GA4 property. Bypasses GTM
     * so custom events fire even when GTM is blocked.
     */
    initializeGA4() {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { dataLayer.push(arguments); };

      if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
        document.head.appendChild(script);
      }

      if (!window._hoaGA4Configured) {
        window._hoaGA4Configured = true;
        gtag('js', new Date());
        gtag('config', GA4_MEASUREMENT_ID);
      }
    }

    /**
     * Tab-scoped session id (sessionStorage). Aligns with GA4's
     * definition of a session.
     */
    getOrCreateSessionId() {
      try {
        const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (existing) return existing;

        const id = `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem(SESSION_STORAGE_KEY, id);
        return id;
      } catch (_e) {
        return `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
    }

    // ───────────────────────────── Events ─────────────────────────────

    /**
     * Fires `hoa_add_to_cart`.
     *
     * @param {Object} params
     * @param {string} params.item_id            Variant id
     * @param {string} params.item_name          Product title
     * @param {string} params.item_variant       Variant title
     * @param {string} params.source_component   Surface identifier (pdp_main, pcp_card, ...)
     * @param {number|string} params.price       Unit price (numeric)
     * @param {number|null} [params.position]    1-based index in a list, optional
     * @param {boolean} [params.oos=false]       True when the waitlist modal was shown
     * @param {string|number|null} [params.selling_plan_id=null] Subscription
     *   selling plan ID when the add was a subscription purchase, or null for
     *   a one-time purchase.
     * @param {Object}  [extra]                  Any additional event params, merged in as-is
     */
    trackAddToCart({ item_id, item_name, item_variant, source_component, price, position = null, oos = false, selling_plan_id = null, ...extra }) {
      const parsedPrice = parseFloat(price) || 0;

      const sellingPlanId =
        selling_plan_id != null && String(selling_plan_id).trim() !== ''
          ? selling_plan_id
          : null;

      const eventData = {
        item_currency:    window.Shopify?.currency?.active || 'USD',
        value:            parsedPrice,
        item_id:          item_id     || '',
        item_name:        item_name   || '',
        item_variant:     item_variant|| '',
        price:            parsedPrice,
        quantity:         1,
        source_component: source_component || '',
        page_path:        window.location.pathname,
        ...(position != null && { position: parseInt(position, 10) }),
        oos:              Boolean(oos),
        selling_plan_id:  sellingPlanId,
        hoa_session_id:   this.sessionId,  // session_id is reserved in GA4
        ...extra
      };

      if (typeof gtag !== 'function') return;
      gtag('event', 'hoa_add_to_cart', eventData);
    }

    /**
     * Per-unit price (dollars) from a /cart/add.js line item. Reflects Skio
     * selling-plan discounts, so it's preferred over data-track-price. Fields
     * are integer cents.
     */
    lineItemUnitPrice(line) {
      if (!line) return null;
      const alloc = line.selling_plan_allocation;
      let cents = alloc ? (alloc.per_delivery_price ?? alloc.price ?? null) : null;
      if (cents == null) cents = line.final_price ?? line.price;
      return cents != null ? parseFloat(cents) / 100 : null;
    }

    /**
     * Subscription selling-plan id from a /cart/add.js line item, or null for
     * a one-time purchase. Handles both the nested object and the flat id.
     */
    lineItemSellingPlanId(line) {
      if (!line) return null;
      const alloc = line.selling_plan_allocation;
      if (!alloc) return null;
      return alloc.selling_plan?.id ?? alloc.selling_plan_id ?? null;
    }

    /**
     * Reads data-track-* attributes from `element` (or its closest
     * ancestor with `[data-track-atc]`) and fires trackAddToCart.
     *
     * priceOverride (dollars), when given, replaces data-track-price.
     * sellingPlanIdOverride, when given (e.g. from the cart line item), is the
     * authoritative selling plan; otherwise it's read live from the form's
     * hidden input[name="selling_plan"], falling back to data-track-selling-plan-id.
     */
    trackAddToCartFromElement(element, priceOverride = null, sellingPlanIdOverride = null) {
      const tracked = element?.closest?.('[data-track-atc]') || element;
      if (!tracked?.dataset) return false;

      const d = tracked.dataset;
      if (!d.trackItemName && !d.trackSource) return false;

      const form =
        (tracked.closest && tracked.closest('form')) ||
        (tracked.querySelector && tracked.querySelector('form'));
      const sellingPlanFromForm =
        form?.querySelector?.('input[name="selling_plan"]')?.value;
      const sellingPlanId =
        sellingPlanIdOverride || sellingPlanFromForm || d.trackSellingPlanId || null;

      this.trackAddToCart({
        item_id:          d.trackItemId,
        item_name:        d.trackItemName,
        item_variant:     d.trackItemVariant,
        source_component: d.trackSource,
        price:            priceOverride != null ? priceOverride : d.trackPrice,
        position:         d.trackPosition,
        oos:              d.trackOos === 'true',
        selling_plan_id:  sellingPlanId
      });
      return true;
    }

    /** Fires `cart_drawer_opened` on cart-drawer open. */
    trackCartDrawerOpened() {
      this._trackCartDrawerEvent('cart_drawer_opened');
    }

    /** Fires `cart_drawer_closed` on cart-drawer close. */
    trackCartDrawerClosed() {
      this._trackCartDrawerEvent('cart_drawer_closed');
    }

    _trackCartDrawerEvent(eventName) {
      const payload = {
        page_path:      window.location.pathname,
        hoa_session_id: this.sessionId,
        currency:       window.Shopify?.currency?.active || 'USD'
      };
      if (typeof gtag !== 'function') return;
      gtag('event', eventName, payload);
    }

    // ──────────────────── Global delegated listeners ──────────────────

    bindGlobalListeners() {
      // product-form dispatches `variant:added` (bubbles) after a successful
      // /cart/add.js. Walk composedPath to find the originating form — more
      // reliable than e.target.closest('form'), since some dispatch sites
      // call dispatchEvent on window, leaving e.target as documentElement.
      document.documentElement.addEventListener('variant:added', (e) => {
        const path = e.composedPath ? e.composedPath() : [];
        const form = path.find(el => el instanceof Element && el.matches('form'))
          || e.target?.closest?.('form')
          || e.target;

        if (!form) return;

        // Cart line item carries the real charged price (incl. subscriptions)
        // and the selling plan allocation when it's a subscription add
        const v = e.detail && e.detail.variant;
        const unitPrice = this.lineItemUnitPrice(v);
        const sellingPlanId = this.lineItemSellingPlanId(v);

        const trackableEl = form.querySelector('[data-track-atc]') || form.closest('[data-track-atc]');
        if (trackableEl) {
          this.trackAddToCartFromElement(trackableEl, unitPrice, sellingPlanId);
          return;
        }

        // Fallback for forms whose button lacks [data-track-atc]: still count
        // the add, using a generic source_component.
        if (v) {
          this.trackAddToCart({
            item_id:          v.id || v.variant_id || '',
            item_name:        v.product_title || v.title || '',
            item_variant:     v.variant_title || '',
            source_component: 'product_form',
            price:            unitPrice != null ? unitPrice : 0,
            selling_plan_id:  sellingPlanId
          });
        }
      });

      // Keep data-track-* in sync with the selected variant (Focal fires
      // `variant:changed` on the form). Price here is the one-time variant
      // price; the authoritative charged price is applied at add time.
      document.documentElement.addEventListener('variant:changed', (e) => {
        const variant = e.detail && e.detail.variant;
        if (!variant) return;

        const path = e.composedPath ? e.composedPath() : [];
        const form = path.find(el => el instanceof Element && el.matches('form'))
          || (e.target && e.target.closest && e.target.closest('form'))
          || (e.target instanceof Element ? e.target : null);
        if (!form) return;

        const btn = form.querySelector('[data-track-atc]');
        if (!btn) return;

        if (variant.id != null)    btn.setAttribute('data-track-item-id', variant.id);
        if (variant.title != null) btn.setAttribute('data-track-item-variant', variant.title);
        if (variant.price != null) btn.setAttribute('data-track-price', parseFloat(variant.price) / 100);
      });

      // Skio plan pickers add via their own fetch and fire `skio::added-to-cart`
      // (not variant:added). detail.response is the /cart/add.js payload.
      // Mutually exclusive with the native form submit, so no double counting.
      document.documentElement.addEventListener('skio::added-to-cart', (e) => {
        const resp = e.detail && e.detail.response;
        if (!resp) return;
        const items = Array.isArray(resp.items) ? resp.items : [resp];

        items.forEach((line) => {
          if (!line) return;
          const unit = this.lineItemUnitPrice(line);
          this.trackAddToCart({
            item_id:          line.id || line.variant_id || '',
            item_name:        line.product_title || line.title || '',
            item_variant:     line.variant_title || '',
            source_component: 'pdp_main', // same PDP surface as the native button
            price:            unit != null ? unit : 0,
            selling_plan_id:  this.lineItemSellingPlanId(line)
          });
        });
      });

      this.observeCartDrawer();
    }

    /**
     * Tracks cart-drawer open/close.
     */
    observeCartDrawer() {
      const isCartDrawer = (el) =>
        el instanceof Element &&
        (el.id === 'mini-cart' || (typeof el.matches === 'function' && el.matches('cart-drawer')));

      document.documentElement.addEventListener('openable-element:open', (e) => {
        if (isCartDrawer(e.target)) this.trackCartDrawerOpened();
      });
      document.documentElement.addEventListener('openable-element:close', (e) => {
        if (isCartDrawer(e.target)) this.trackCartDrawerClosed();
      });
    }
  }

  window.SiteAnalytics = new SiteAnalytics();
}
